import {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import type {EventPromotionReducerState, PrintFlyerPoster, PromotionStep, PromotionSteps} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';
import {PromotionType} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';
import type {EventHashedID, EventVO} from '@Components/events/events.types';
import {isEventPromotionPage, isManageEventPage} from '@Libraries/my-stuff/my-stuff-library';
import {LoadingStates} from '@Utils/loading.util';
import type {ManageEventPageReduxState} from '@Components/mystuff-manage-event-page/mystuff-manage-event-page.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {isPAYGUser} from '@Utils/user.util';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {getAjaxParamsForUpdatingEmailCampaignContent, getCampaignPublishWizardUri, isCampaignDeleted, isCampaignSuspended, isDraftCampaign, openCampaignInWizard} from '@Libraries/email-campaigns-library';
import type {OneClickSocialFormatSelection} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel.types';
import {OneClickPromoItemState} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel.types';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {isPostPublished} from '@Libraries/social-media-post.library';
import {EventUpsellType, openEventsUpsellDialog, openUserEventPreviewDialog} from "@Libraries/events.library";
import {openOneClickPromotionModal} from "@Modals/event-one-click-promotion-modal/event-one-click-promotion-modal.library";
import {DOWNLOAD_MODAL_PANEL_ID, openNewDownloadModal} from '@Modals/download-modal/download-modal.library';
import {openEmbedEventsScheduleModal} from '@Modals/embed-events-schedule-modal/embed-events-schedule-modal.library';
import {GA4EventName} from '@Libraries/ga-events';

export const PROMOTION_MYSTUFF_CONTAINER_QUERY: ContainerQueryObject = {
  navXS: {
    maxWidth: 450,
  },

  small: {
    minWidth: 0,
    maxWidth: 600,
  },

  mediumAndLaptopSize: {
    minWidth: 601,
    maxWidth: 950,
  },

  xlarge: {
    minWidth: 951,
  },
};

export const EVENT_PROMOTION_LEARN_MORE_PAGE_URL = 'https://www.postermywall.com/index.php/m/event-marketing-tool/#how-can-i-promote-my-event';

export const isEventLoadedInPromotePage = (reduxState: EventPromotionReducerState, eventPageHash: string, eventHashedId: EventHashedID): boolean => {
  return isEventPromotionPage(eventPageHash) && reduxState.event.hashedID === eventHashedId && reduxState.loadingState === LoadingStates.LOADED;
};

export const isEventLoadedInManagePage = (reduxState: ManageEventPageReduxState, eventPageHash: string, eventHashedId: EventHashedID): boolean => {
  return isManageEventPage(eventPageHash) && reduxState.event.hashedID === eventHashedId && reduxState.loadingState === LoadingStates.LOADED;
};

export const isPromotionStepCompletedOrSkipped = (step: PromotionStep): boolean => {
  return step.isCompleted || step.isSkipped;
};

export const getPromotionStepFromType = (promotionSteps: PromotionSteps, promotionType: PromotionType): PromotionStep | undefined => {
  return promotionSteps.find((step) => {
    return step.type === promotionType;
  });
};

/** The steps with a completed one un-checked, as the server serves a social or email step that only drafts back. */
export const getStepsWithCompletedStepUnchecked = (promotionSteps: PromotionSteps, promotionType: PromotionType): PromotionSteps => {
  return promotionSteps.map((step) => {
    return step.type === promotionType && step.isCompleted ? {...step, isCompleted: false, isSkipped: false} : step;
  });
};

/** A new array in sortOrder — never mutates the Redux-owned steps array it is handed. */
export const sortPromotionSteps = (steps: PromotionSteps): PromotionSteps => {
  return [...steps].sort((stepA, stepB) => {
    return stepA.sortOrder - stepB.sortOrder;
  });
};

export const doesStepHavePremiumUpselling = (step: PromotionStep): boolean => {
  return step.type === PromotionType.EMAIL_CAMPAIGNS && isPAYGUser();
};

const SEEN_COMPLETED_PROMOTION_STEPS_KEY_PREFIX = 'pmw_promote_seen_completed_steps_';
const SEEN_COMPLETED_OTHER_PROMOTION_STEPS_KEY_PREFIX = 'pmw_promote_seen_completed_other_steps_';

/** The steps this browser last showed as complete for the event; null before its first visit or without storage. */
export const getSeenCompletedPromotionSteps = (eventHashedId: EventHashedID): PromotionType[] | null => {
  return getStoredPromotionTypes(`${SEEN_COMPLETED_PROMOTION_STEPS_KEY_PREFIX}${eventHashedId}`);
};

export const setSeenCompletedPromotionSteps = (eventHashedId: EventHashedID, promotionTypes: PromotionType[]): void => {
  storePromotionTypes(`${SEEN_COMPLETED_PROMOTION_STEPS_KEY_PREFIX}${eventHashedId}`, promotionTypes);
};

/** The same for the uncounted "Other ways to promote" steps, kept apart so a browser's earlier visits never read as new. */
export const getSeenCompletedOtherPromotionSteps = (eventHashedId: EventHashedID): PromotionType[] | null => {
  return getStoredPromotionTypes(`${SEEN_COMPLETED_OTHER_PROMOTION_STEPS_KEY_PREFIX}${eventHashedId}`);
};

export const setSeenCompletedOtherPromotionSteps = (eventHashedId: EventHashedID, promotionTypes: PromotionType[]): void => {
  storePromotionTypes(`${SEEN_COMPLETED_OTHER_PROMOTION_STEPS_KEY_PREFIX}${eventHashedId}`, promotionTypes);
};

const getStoredPromotionTypes = (storageKey: string): PromotionType[] | null => {
  try {
    const storedTypes: unknown = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
    if (!Array.isArray(storedTypes)) {
      return null;
    }

    const promotionTypes: unknown[] = Object.values(PromotionType);
    return storedTypes.filter((storedType): storedType is PromotionType => promotionTypes.includes(storedType));
  } catch {
    return null;
  }
};

const storePromotionTypes = (storageKey: string, promotionTypes: PromotionType[]): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(promotionTypes));
  } catch {
    // Without storage every visit is a first visit, so nothing celebrates.
  }
};

export const getDummyPrintFlyerItemFromHID = (hashedId: string): PrintFlyerPoster => {
  return {
    posterHashedID: hashedId,
    posterName: '',
    isVideo: false,
    idcreator: -1,
    posterId: -1,
    isPreviewOutdated: true,
    previewImageURL: '',
    previewVideoURL: '',
    isLoading: true,
  };
};

/** Opens the existing download modal for a single print-flyer poster; download marks PRINT_FLYERS complete (dispatchDownloadButtonClickedEvent). */
export const openDownloadModalForFlyer = (posterHashedId: string): void => {
  openNewDownloadModal({
    id: DOWNLOAD_MODAL_PANEL_ID,
    posterHashedId,
    isOpenedInEditor: false,
    isOpenedInModal: true,
    isOpenedInPrintShop: false,
  });
};

export const startOneClickPromotionFlowForEvent = (eventHashedId: EventHashedID): void => {
  if (isPAYGUser()) {
    openEventsUpsellDialog(EventUpsellType.EVENT_ONE_CLICK_PROMOTION);
    return;
  }

  openOneClickPromotionModal({
    eventHashedId,
  });
}

/** The promote page's preview, from its ⋮ menu or mobile header; no promote option, since the viewer is already promoting. */
export const openEventPreviewFromPromotePage = (event: EventVO): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_MYSTUFF_PREVIEW);
  openUserEventPreviewDialog(event, {showPromoteOption: false});
};

export const isOneClickEmailCampaignInErrorState = (campaign: EmailCampaign, campaignOneClickState: OneClickPromoItemState): boolean => {
  return campaignOneClickState === OneClickPromoItemState.ERROR || isCampaignDeleted(campaign) || isCampaignSuspended(campaign);
};

export const isOneClickEmailCampaignInSuccessState = (campaign: EmailCampaign, campaignOneClickState: OneClickPromoItemState): boolean => {
  return campaignOneClickState === OneClickPromoItemState.SUCCESS || (!isDraftCampaign(campaign) && !isOneClickEmailCampaignInErrorState(campaign, campaignOneClickState));
};

export const isOneClickSocialPostInErrorState = (socialPost: SocialPost, postOneClickState: OneClickPromoItemState): boolean => {
  return postOneClickState === OneClickPromoItemState.ERROR || (socialPost.state === PostState.ERROR || socialPost.state === PostState.SCHEDULE_ERROR);
};

export const isOneClickSocialPostInSuccessState = (socialPost: SocialPost, postOneClickState: OneClickPromoItemState): boolean => {
  return postOneClickState === OneClickPromoItemState.SUCCESS || isPostPublished(socialPost.state);
};

export const openEmailCampaignInWizardForEventPromotion = (campaign: EmailCampaign, eventHashedID: EventHashedID): void => {
  window.PMW.redirectUrlCache.setCache(
      window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_CACHE_KEY,
      window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_REDIRECT_URI + `/${eventHashedID}`,
      window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_DEFAULT_CACHE_TIMEOUT_MILLISECONDS
  );

  openCampaignInWizard(getCampaignPublishWizardUri(campaign), UpsellDialogEntryPoint.EVENT_PROMOTION);
}

/** Without a format selection the post's existing slots are kept. */
export const doSaveOneClickSocialPostOnBackend = (post: SocialPost, formatSelection?: OneClickSocialFormatSelection): Promise<void> => {
  const accountIds = post.accounts.map((account) => {
    return account.id;
  });

  return window.PMW.writeLocal('socialmedia/saveSocialPostDetails', {
    idSocialPost: post.id,
    caption: post.caption,
    accountIds: accountIds,
    ...formatSelection,
  });
};

export const doSaveOneClickCampaignOnBackend = (campaign: EmailCampaign): Promise<void> => {
  return window.PMW.writeLocal('emailmarketing/updateEmailCampaignContentAndMailingLists', getAjaxParamsForUpdatingEmailCampaignContent(campaign));
}

/** The Promote page's calendar embed: opens the calendar embed modal and tracks every copy of its code. */
export const openPromotePageCalendarEmbedModal = (): void => {
  openEmbedEventsScheduleModal({
    onCopyEmbeddedCode: (): void => {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CALENDAR_PROMOTE_EMBED);
    },
  });
};
